import { Injectable, inject } from '@angular/core'

import { createStore, sample } from 'effector'
import { Draft, current, produce } from 'immer'

import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { NodeDefinitionNormalized } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { AreaSummary, AreasResponse } from '@flowx/designer/shared/api/area.api'
import { NavigationFlow } from '@flowx/designer/shared/api/navigation-flow.api'
import { AreasEvents } from '@flowx/designer/shared/effector/area/area.events'
import { BpmnBoundaryEventEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-boundary-event.events'
import { BpmnNodeEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-node.events'
import { NodeEvents } from '@flowx/designer/shared/effector/node/node.events'
import { NodeStores } from '@flowx/designer/shared/effector/node/node.stores'
import { Area, AreaPlatform } from '@flowx/designer/shared/entities/nav-area.entity'

import { AreasEffects } from './area.effects'

@Injectable({ providedIn: 'root' })
export class AreaStores {
  readonly areasEffects = inject(AreasEffects)

  readonly selectedPlatform$ = createStore<AreaPlatform>(AreaPlatform.Web, {
    name: 'selectedPlatform$',
  })
    .on(AreasEvents.platformSelected, (_, platform) => platform)
    .reset(AreasEvents.resetSelectedPlatform)

  readonly selectedArea$ = createStore<AreaSummary | null>(null, {
    name: 'selectedArea$',
  })
    .on([AreasEvents.areaClicked, AreasEvents.areaSelectedOnCanvas], (_, area) => area)
    .reset([
      AreasEvents.cleanupAreas,
      BpmnEditorEvents.canvasLeftClick,
      BpmnNodeEvents.nodeLeftClick,
      BpmnNodeEvents.nodeRightClick,
      BpmnBoundaryEventEvents.boundaryRightClick,
      BpmnBoundaryEventEvents.boundaryLeftClick,
      BpmnEditorEvents.swimlaneLeftClick,
      BpmnEditorEvents.swimlaneRightClick,
    ])

  readonly loadingAreas$ = this.areasEffects.loadAreasFx.pending

  readonly nodesOnAreasReady = sample({
    source: {
      nodes: NodeStores.nodeDefinitionsByNavigationAreaId$,
      loadedAreas: this.areasEffects.loadedAreas$,
    },
    filter: ({ loadedAreas }) => loadedAreas,
    fn: ({ nodes }) => nodes,
  })

  // Multiple loadAreasFx calls can be in flight with different platforms (e.g. the stale WEB load
  // triggered by showAreasChange racing the MOBILE load on process designer entry); only responses
  // requested for the currently selected platform may update areas$.
  readonly areasLoadedForSelectedPlatform = sample({
    clock: this.areasEffects.loadAreasFx.done,
    source: this.selectedPlatform$,
    filter: (selectedPlatform, { params }) =>
      (params as { platform: AreaPlatform }).platform === selectedPlatform,
    fn: (_, { result }) => result,
  })

  readonly areas$ = createStore<AreasResponse>(
    new Map<string, { userTasks: Area[]; navigationTree: Area[] }>(),
    {
      name: 'areas$',
    }
  )
    .on(
      this.areasLoadedForSelectedPlatform,
      (_, areas) =>
        new Map(
          Object.entries(areas).map(([key, value]) => [
            key,
            {
              userTasks: value.userTasks || [],
              navigationTree: Area.decorateAreasWithLayoutOptions(value.navigationTree) || [],
            },
          ])
        )
    )
    .on(this.areasEffects.pasteAreasFx.doneData, (state, payload) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(payload.swimlaneDefinitionId)
        swimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(payload.navigationTree)
        swimlaneState.userTasks = payload.userTasks
      })
    )
    .on(this.areasEffects.createAreaFx.doneData, (state, area: Area) =>
      produce(state, (draft) => {
        if (!area.parentNavAreaId) {
          draft.get(area.swimlaneDefinitionId).navigationTree.push(area)
        } else {
          const parent = Area.findAreaById(
            draft.get(area.swimlaneDefinitionId).navigationTree,
            area.parentNavAreaId
          )
          if (parent?.children?.length) {
            parent.children.push(area)
          } else {
            parent.children = [area]
          }
        }
        draft.get(area.swimlaneDefinitionId).navigationTree = Area.decorateAreasWithLayoutOptions(
          draft.get(area.swimlaneDefinitionId).navigationTree
        )
      })
    )
    .on(
      [
        this.areasEffects.createParentAreaFx.doneData,
        this.areasEffects.removeParentProcessAreaFx.doneData,
      ],
      (state, areas: AreasResponse) =>
        produce(state, (draft) => {
          const swimlaneId = Object.keys(areas)[0]
          const swimlaneState = draft.get(swimlaneId)
          swimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
            areas[swimlaneId].navigationTree
          )
          swimlaneState.userTasks = areas[swimlaneId].userTasks || []
        })
    )
    .on(this.areasEffects.removeAreaFx.doneData, (state, area) =>
      produce(state, (draft) => {
        if (!area) {
          return
        }
        const swimlaneState = draft.get(area.swimlaneDefinitionId)

        if (Area.isNodeNavigationArea(area.type)) {
          swimlaneState.userTasks.push({ ...area, parentNavAreaId: null })
          const parent = Area.findAreaById(swimlaneState.navigationTree, area.parentNavAreaId)
          parent.children = parent.children.filter((item) => item.id !== area.id)
        } else {
          if (!area.parentNavAreaId) {
            swimlaneState.navigationTree = swimlaneState.navigationTree.filter(
              (item) => item.id !== area.id
            )
          } else {
            const parent = Area.findAreaById(swimlaneState.navigationTree, area.parentNavAreaId)
            parent.children = parent.children.filter((item) => item.id !== area.id)
          }

          const userTasksToRemove = Area.getDeeplyNestedViewportAreas([area])

          const existingUserTasks = swimlaneState.userTasks
          existingUserTasks.push(
            ...userTasksToRemove.map((item) => ({ ...item, parentNavAreaId: null }))
          )
        }
        swimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
          swimlaneState.navigationTree
        )
      })
    )
    .on(this.areasEffects.updateAreaSettingsFx.done, (state, payload) =>
      produce(state, (draft) => {
        const area = payload.params
        const areaToUpdate = Area.findAreaById(
          draft.get(area.swimlaneDefinitionId).navigationTree,
          area.id
        )
        if (area.label) {
          areaToUpdate.label = area.label
        }
        areaToUpdate.navigationFlowIds = area.navigationFlowIds
      })
    )
    .on(AreasEvents.updateAreaSubprocesses, (state, payload) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(payload.swimlaneDefinitionId)
        if (!swimlaneState) {
          return
        }
        const area = Area.findAreaById(swimlaneState.navigationTree, payload.areaId)
        if (area) {
          area.subprocesses = payload.subprocesses
        }
      })
    )

    .on(this.areasEffects.reorderAreasFx.doneData, (state, payload) => {
      const {
        swimlaneDefinitionId,
        areaId,
        newParentId,
        oldParentId,
        previousSiblingId,
        nextSiblingId,
        toIndex,
        fromIndex,
      } = payload

      return produce(state, (draft) => {
        const swimlaneState = draft.get(swimlaneDefinitionId)
        const newParent = Area.findAreaById(swimlaneState.navigationTree, newParentId)
        const oldParent = Area.findAreaById(swimlaneState.navigationTree, oldParentId)
        const nextSibling = Area.findAreaById(swimlaneState.navigationTree, nextSiblingId)
        const previousSibling = Area.findAreaById(swimlaneState.navigationTree, previousSiblingId)

        const movedArea =
          Area.findAreaById(swimlaneState.navigationTree, areaId) ||
          swimlaneState.userTasks.find((item) => item.id === areaId)

        // Container stays the same only update siblings
        if (newParentId === oldParentId) {
          if (!newParent) {
            // reorder is in root
            swimlaneState.navigationTree = this.reoderAreasInsideContainer(
              movedArea,
              toIndex,
              fromIndex,
              swimlaneState.navigationTree,
              nextSibling,
              previousSibling
            )
          } else {
            newParent.children = this.reoderAreasInsideContainer(
              movedArea,
              toIndex,
              fromIndex,
              newParent.children || [],
              nextSibling,
              previousSibling
            )
          }
        } else {
          // Container changed
          if (oldParentId) {
            oldParent.children = Area.removeAreaFromContainer(oldParent, areaId)
          } else {
            swimlaneState.userTasks = swimlaneState.userTasks.filter((item) => item.id !== areaId)
            swimlaneState.navigationTree = swimlaneState.navigationTree.filter(
              (item) => item.id !== areaId
            )
          }
          if (!newParent) {
            swimlaneState.navigationTree = this.moveAreaToContainer(
              movedArea,
              newParentId,
              toIndex,
              swimlaneState.navigationTree,
              nextSibling,
              previousSibling
            )
          } else {
            newParent.children = this.moveAreaToContainer(
              movedArea,
              newParentId,
              toIndex,
              newParent.children || [],
              nextSibling,
              previousSibling
            )
          }
        }
        swimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
          swimlaneState.navigationTree
        )
      })
    })
    .on(AreasEvents.resizeArea, (state, payload) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(payload.swimlaneDefinitionId)
        const area = Area.findAreaById(swimlaneState?.navigationTree, payload.areaId)
        if (area) {
          area.layoutOptions = {
            ...area.layoutOptions,
            x: payload.layoutOptions.x,
            y: payload.layoutOptions.y,
          }

          if (area.parentNavAreaId) {
            swimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
              swimlaneState.navigationTree
            )
          }
        }
      })
    )
    .on(AreasEvents.filterByNavigationFlows, (state, payload) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(payload.swimlaneId)
        swimlaneState.navigationTree = this.disableAreasByNavigationFlows(
          swimlaneState.navigationTree,
          payload.navigationFlowIds
        )
      })
    )
    .on(AreasEvents.toggleSubprocessesVisibility, (state, { swimlaneId, areaId }) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(swimlaneId)
        const area = Area.findAreaById(swimlaneState?.navigationTree, areaId)

        if (area) {
          area.meta = {
            ...(area.meta || {}),
            subprocessesVisible: !area.meta?.subprocessesVisible,
          }
        }
      })
    )
    .on(BpmnNodeEvents.swimlaneChanged, (state, updates) =>
      produce(state, (draft) => {
        updates.forEach((update) => {
          const { areaId, oldSwimlaneId, newSwimlaneId, layoutOptions } = update
          const oldSwimlaneState = draft.get(oldSwimlaneId)
          const newSwimlaneState = draft.get(newSwimlaneId)
          if (!oldSwimlaneState || !newSwimlaneState) {
            return
          }

          const area =
            Area.findAreaById(oldSwimlaneState?.navigationTree, areaId) ||
            oldSwimlaneState.userTasks.find((item) => item.id === areaId)

          if (area) {
            oldSwimlaneState.userTasks = oldSwimlaneState.userTasks.filter(
              (_area) => _area.id !== areaId
            )
            const parent = Area.findAreaById(oldSwimlaneState.navigationTree, area.parentNavAreaId)
            if (parent) {
              parent.children = parent.children.filter((_area) => _area.id !== areaId)
            }
            oldSwimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
              oldSwimlaneState.navigationTree
            )
            area.layoutOptions = { ...area.layoutOptions, x: layoutOptions.x, y: layoutOptions.y }
            newSwimlaneState.userTasks = [
              ...(newSwimlaneState.userTasks || []),
              { ...area, parentNavAreaId: null },
            ]
            newSwimlaneState.navigationTree = Area.decorateAreasWithLayoutOptions(
              newSwimlaneState.navigationTree
            )
          }
        })
      })
    )
    .on(this.nodesOnAreasReady, (state, nodes: Map<number, NodeDefinitionNormalized>) =>
      produce(state, (draft) => {
        draft.forEach((swimlane) => {
          swimlane.userTasks = swimlane.userTasks.map((userTask) => ({
            ...userTask,
            layoutOptions: { ...userTask.layoutOptions, ...nodes[userTask.id]?.layoutOptions },
          }))
          swimlane.navigationTree = Area.decorateAreasWithLayoutOptions(
            current(swimlane.navigationTree),
            nodes
          )
        })
      })
    )
    .on(NodeEvents.nodeRenamed, (state, { name, navigationAreaId, swimlaneDefinitionId }) =>
      produce(state, (draft) => {
        const swimlaneState = draft.get(swimlaneDefinitionId)
        const area =
          Area.findAreaById(swimlaneState?.navigationTree, navigationAreaId) ||
          swimlaneState?.userTasks?.find((item) => item.id === navigationAreaId)
        if (area) {
          area.label = name
        }
      })
    )

    .on(AreasEvents.cleanupAreas, () => new Map([]))

  readonly allAreas$ = this.areas$.map((areas) =>
    Array.from(areas.values()).flatMap((area) => area.navigationTree)
  )

  reoderAreasInsideContainer(
    movedArea: Draft<Area>,
    toIndex: number,
    fromIndex: number,
    siblings: Draft<Area[]>,
    nextSibling: Draft<Area>,
    previousSibling: Draft<Area>
  ): Draft<Area[]> {
    siblings.splice(toIndex, 0, siblings.splice(fromIndex, 1)[0])

    movedArea.nextSiblingId = nextSibling?.id || null
    if (previousSibling) {
      previousSibling.nextSiblingId = movedArea.id || null
    }
    if (nextSibling) {
      nextSibling.nextSiblingId = siblings[toIndex + 1]?.id || null
    }

    return siblings
  }

  moveAreaToContainer(
    movedArea: Area,
    newParentId: number,
    toIndex: number,
    siblings: Area[],
    nextSibling: Area,
    previousSibling: Area
  ): Draft<Area[]> {
    siblings.splice(toIndex > -1 ? toIndex : 0, 0, movedArea)

    movedArea.nextSiblingId = nextSibling?.id || null
    if (previousSibling) {
      previousSibling.nextSiblingId = movedArea.id || null
    }
    if (nextSibling) {
      nextSibling.nextSiblingId = siblings[toIndex + 1]?.id || null
    }

    movedArea.parentNavAreaId = newParentId

    return siblings
  }

  disableAreasByNavigationFlows(
    navigationTree: Draft<Area[]>,
    navFlowsIds: NavigationFlow['id'][]
  ): Draft<Area[]> {
    return navigationTree?.reduce(
      (acc, area) => [
        ...acc,
        {
          ...area,
          meta: {
            ...area.meta,
            isDisabled:
              navFlowsIds.length > 0 &&
              !area.navigationFlowIds?.some((id) =>
                navFlowsIds?.find((selectedNavFlowId) => selectedNavFlowId === id)
              ),
          },
          children: this.disableAreasByNavigationFlows(area.children, navFlowsIds),
        },
      ],
      []
    )
  }
}
